import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import homeData from "../../data/home.json";
import {
  Award,
  MapPin,
  Eye,
  ArrowRight,
  Quote,
  Download,
  CheckCircle,
  Sparkles,
  TrendingUp,
  Globe,
  Shield,
  ChevronRight,
  Star,
} from "lucide-react";

/* ─────────────────────────────────────────────
 ICON MAP
───────────────────────────────────────────── */
const ICON_MAP = {
  Award,
  MapPin,
  Eye,
  Shield,
  Globe,
  Star,
  TrendingUp,
  CheckCircle,
};

/* ─────────────────────────────────────────────
 ANIMATED SVG CIRCLE (draw stroke on reveal)
───────────────────────────────────────────── */
const AnimatedCircle = ({ color, size = 64, isActive }) => {
  const circleRef = useRef(null);
  const radius = size / 2 - 4;
  const circum = 2 * Math.PI * radius;

  useEffect(() => {
    if (!circleRef.current) return;
    gsap.to(circleRef.current, {
      strokeDashoffset: isActive ? 0 : circum,
      duration: isActive ? 0.8 : 0.4,
      ease: isActive ? "power3.out" : "power2.in",
    });
  }, [isActive, circum]);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="absolute inset-0 -rotate-90"
    >
      {/* Track */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={`${color}20`}
        strokeWidth="1.5"
      />
      {/* Progress */}
      <circle
        ref={circleRef}
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeDasharray={circum}
        strokeDashoffset={circum}
        strokeLinecap="round"
      />
    </svg>
  );
};

/* ─────────────────────────────────────────────
 REASON CARD
───────────────────────────────────────────── */
const ReasonCard = ({ item, index, isActive, onEnter }) => {
  const cardRef = useRef(null);
  const iconRef = useRef(null);
  const lineRef = useRef(null);
  const textRef = useRef(null);
  const statRef = useRef(null);
  const IconC = ICON_MAP[item.icon] || Award;

  /* Scroll reveal — stagger per card */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        cardRef.current,
        { x: 60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          delay: index * 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: cardRef.current,
            start: "top 80%",
          },
        },
      );
    }, cardRef);
    return () => ctx.revert();
  }, [index]);

  /* Active state animations */
  useEffect(() => {
    if (!cardRef.current) return;

    if (isActive) {
      gsap.to(cardRef.current, {
        borderColor: `${item.color}50`,
        backgroundColor: `${item.color}08`,
        duration: 0.4,
        ease: "power2.out",
      });
      gsap.to(iconRef.current, {
        scale: 1.1,
        rotate: 5,
        duration: 0.4,
        ease: "back.out(1.7)",
      });
      gsap.to(lineRef.current, {
        scaleX: 1,
        duration: 0.5,
        ease: "power3.out",
      });
      gsap.fromTo(
        textRef.current,
        { opacity: 0.6 },
        { opacity: 1, duration: 0.3 },
      );
      gsap.fromTo(
        statRef.current,
        { y: 5, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.4, ease: "power3.out" },
      );
    } else {
      gsap.to(cardRef.current, {
        borderColor: "rgba(226,232,240,0.8)",
        backgroundColor: "rgba(255,255,255,1)",
        duration: 0.4,
        ease: "power2.out",
      });
      gsap.to(iconRef.current, {
        scale: 1,
        rotate: 0,
        duration: 0.35,
        ease: "power2.out",
      });
      gsap.to(lineRef.current, {
        scaleX: 0,
        duration: 0.3,
        ease: "power2.in",
      });
      gsap.to(statRef.current, {
        opacity: 0,
        y: 5,
        duration: 0.2,
      });
    }
  }, [isActive, item.color]);

  return (
    <div
      ref={cardRef}
      onMouseEnter={onEnter}
      className="reason-card relative rounded-2xl p-6 cursor-pointer
 transition-shadow duration-300 hover:shadow-card-hover bg-white border border-slate-200"
      style={{
        backdropFilter: "blur(16px)",
        opacity: 0,
      }}
    >
      {/* Left colored border accent */}
      <div
        className="absolute left-0 top-4 bottom-4 w-[3px] rounded-full"
        style={{ background: item.color, opacity: isActive ? 1 : 0.3 }}
      />

      <div className="flex items-start gap-5">
        {/* Icon with animated circle */}
        <div
          ref={iconRef}
          className="relative flex-shrink-0"
          style={{ width: 56, height: 56 }}
        >
          <AnimatedCircle color={item.color} size={56} isActive={isActive} />
          <div
            className="absolute inset-0 flex items-center justify-center
 rounded-full"
            style={{ background: `${item.color}15` }}
          >
            <IconC size={22} style={{ color: item.color }} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 space-y-2 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-bold text-slate-900 text-lg leading-snug">
              {item.title}
            </h3>
            {/* Stat badge */}
            <div
              ref={statRef}
              className="flex-shrink-0 text-right"
              style={{ opacity: 0 }}
            >
              <p
                className="font-black font-mono text-lg leading-none"
                style={{ color: item.color }}
              >
                {item.stat.value}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {item.stat.label}
              </p>
            </div>
          </div>

          {/* Animated underline */}
          <div
            ref={lineRef}
            className="h-px rounded-full origin-left"
            style={{
              background: `linear-gradient(to right, ${item.color}60, transparent)`,
              transform: "scaleX(0)",
            }}
          />

          <p
            ref={textRef}
            className="text-sm text-slate-600 leading-relaxed"
            style={{ opacity: 0.6 }}
          >
            {item.description}
          </p>
        </div>
      </div>

      {/* Hover glow */}
      {isActive && (
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            background: `radial-gradient(ellipse 80% 60% at 20% 50%,
 ${item.color}08 0%, transparent 70%)`,
          }}
        />
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
 STICKY LEFT COLUMN
───────────────────────────────────────────── */
const StickyLeft = ({ data, activeIndex }) => {
  const colRef = useRef(null);
  const headRef = useRef(null);
  const lineRef = useRef(null);
  const subRef = useRef(null);
  const ctaRef = useRef(null);
  const progressRef = useRef([]);
  const orbRef = useRef(null);

  /* Entry animation */
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: colRef.current,
          start: "top 70%",
        },
      });

      tl.fromTo(
        headRef.current?.children ?? [],
        { y: 50, opacity: 0, skewY: 2 },
        {
          y: 0,
          opacity: 1,
          skewY: 0,
          stagger: 0.12,
          duration: 0.8,
          ease: "power4.out",
        },
      )
        .fromTo(
          lineRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.7,
            ease: "power3.out",
            transformOrigin: "left",
          },
          "-=0.1",
        )
        .fromTo(
          subRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
          "-=0.1",
        )
        .fromTo(
          progressRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            stagger: 0.1,
            duration: 0.5,
            ease: "power3.out",
            transformOrigin: "left",
          },
          "-=0.2",
        )
        .fromTo(
          ctaRef.current?.children ?? [],
          { y: 20, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.1,
            duration: 0.5,
            ease: "power3.out",
          },
          "-=0.2",
        );

      /* Orb float */
      gsap.to(orbRef.current, {
        y: -20,
        x: 10,
        duration: 4,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });
    }, colRef);

    return () => ctx.revert();
  }, []);

  /* Active indicator dots */
  useEffect(() => {
    progressRef.current.forEach((el, i) => {
      if (!el) return;
      gsap.to(el, {
        scaleX: i === activeIndex ? 1 : 0.3,
        opacity: i === activeIndex ? 1 : 0.3,
        backgroundColor:
          i === activeIndex ? data.items[i].color : "rgba(255,255,255,0.2)",
        duration: 0.4,
        ease: "power2.out",
      });
    });
  }, [activeIndex, data.items]);

  return (
    <div ref={colRef} className="lg:sticky lg:top-28 space-y-8">
      {/* Floating orb */}
      <div
        ref={orbRef}
        className="absolute -top-10 -left-10 w-40 h-40
 rounded-full pointer-events-none hidden lg:block"
        style={{
          background:
            "radial-gradient(circle, rgba(29,78,216,0.15) 0%, transparent 70%)",
          filter: "blur(30px)",
        }}
      />

      {/* Section label - removed */}

      {/* Headline */}
      <div ref={headRef} className="overflow-hidden space-y-1">
        <h2
          className="font-display font-black text-slate-900 leading-tight"
          style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)" }}
        >
          {data.headline}
        </h2>
        <h2
          className="font-display font-black text-gradient-primary leading-tight"
          style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)" }}
        >
          {data.headlineAccent}
        </h2>
      </div>

      {/* Divider */}
      <div
        ref={lineRef}
        className="h-[2px] w-16 rounded-full"
        style={{
          background: "linear-gradient(90deg, #1d4ed8, #f59e0b)",
          transform: "scaleX(0)",
        }}
      />

      {/* Sub text */}
      <p
        ref={subRef}
        className="text-slate-600 leading-relaxed text-[15px] max-w-sm"
        style={{ opacity: 0 }}
      >
        With deep expertise across Ethiopia and the wider Horn of Africa, we
        deliver research-driven, locally contextualised solutions that create
        lasting impact.
      </p>

      {/* Progress indicators */}
      <div className="space-y-2.5">
        {data.items.map((item, i) => (
          <div key={item.id} className="flex items-center gap-3">
            <div
              ref={(el) => (progressRef.current[i] = el)}
              className="h-[3px] w-12 rounded-full origin-left"
              style={{
                background: "rgba(255,255,255,0.2)",
                transform: "scaleX(0)",
              }}
            />
            <span
              className="text-xs text-slate-500 transition-colors duration-300"
              style={{
                color: i === activeIndex ? item.color : undefined,
              }}
            >
              {item.title}
            </span>
          </div>
        ))}
      </div>

      {/* CTA buttons */}
      <div ref={ctaRef} className="flex flex-col sm:flex-row gap-3">
        <Link
          to={data.cta.href}
          className="group inline-flex items-center gap-2.5 px-6 py-3.5
 bg-primary-600 hover:bg-primary-500 text-white font-semibold
 rounded-xl text-sm transition-all duration-300
 hover:shadow-glow-primary hover:-translate-y-0.5 overflow-hidden relative"
        >
          <span
            className="absolute inset-0 translate-x-[-100%]
 group-hover:translate-x-[100%] transition-transform duration-700
 bg-gradient-to-r from-transparent via-white/15 to-transparent"
          />
          <span>{data.cta.text}</span>
          <ArrowRight
            size={14}
            className="transition-transform duration-300
 group-hover:translate-x-1"
          />
        </Link>

        <a
          href="#"
          download
          className="inline-flex items-center gap-2.5 px-6 py-3.5
 border border-secondary-300 text-secondary-600
 hover:bg-secondary-50 font-medium rounded-xl text-sm
 transition-all duration-300 hover:-translate-y-0.5"
        >
          <Download size={14} />
          <span>Capacity Statement</span>
        </a>
      </div>

      {/* Trust badges */}
      <div className="flex flex-wrap gap-2 pt-2">
        {["UN Agency Partner", "Evidence-Based", "Ethiopia-Wide"].map(
          (badge, i) => (
            <div key={i} className="flex items-center gap-1.5 tag">
              <CheckCircle size={10} className="text-secondary-400" />
              {badge}
            </div>
          ),
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 QUOTE BANNER
───────────────────────────────────────────── */
const QuoteBanner = ({ quote }) => {
  const ref = useRef(null);
  const textRef = useRef(null);
  const quoteRef = useRef(null);
  const lineRef = useRef(null);
  const bgRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      /* Clip-path reveal */
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: ref.current,
          start: "top 80%",
        },
      });

      tl.fromTo(
        ref.current,
        {
          clipPath: "polygon(0 50%, 100% 50%, 100% 50%, 0 50%)",
        },
        {
          clipPath: "polygon(0 0%, 100% 0%, 100% 100%, 0 100%)",
          duration: 0.8,
          ease: "power4.inOut",
        },
      )
        .fromTo(
          quoteRef.current,
          { scale: 0, opacity: 0, rotate: -20 },
          {
            scale: 1,
            opacity: 1,
            rotate: 0,
            duration: 0.5,
            ease: "back.out(1.7)",
          },
          "-=0.4",
        )
        .fromTo(
          lineRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.6,
            ease: "power3.out",
            transformOrigin: "left",
          },
          "-=0.3",
        )
        .fromTo(
          textRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
          "-=0.3",
        );

      /* BG parallax */
      gsap.to(bgRef.current, {
        yPercent: -20,
        ease: "none",
        scrollTrigger: {
          trigger: ref.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.5,
        },
      });

      /* Shimmer animation */
      gsap.fromTo(
        ref.current.querySelector(".shimmer-line"),
        { x: "-100%" },
        {
          x: "100%",
          duration: 2,
          ease: "none",
          repeat: -1,
          delay: 1,
          repeatDelay: 3,
        },
      );
    }, ref);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={ref}
      className="relative overflow-hidden rounded-3xl my-16 lg:my-24"
      style={{
        clipPath: "polygon(0 50%, 100% 50%, 100% 50%, 0 50%)",
      }}
    >
      {/* BG layer */}
      <div
        ref={bgRef}
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 50%, #60a5fa 100%)",
        }}
      />

      {/* Dot pattern */}
      <div className="absolute inset-0 dot-pattern opacity-20" />

      {/* Diagonal lines decoration */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
 repeating-linear-gradient(
 45deg,
 transparent,
 transparent 60px,
 rgba(255,255,255,0.02) 60px,
 rgba(255,255,255,0.02) 61px
 )
`,
        }}
      />

      {/* Shimmer sweep */}
      <div
        className="shimmer-line absolute inset-y-0 w-20 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)",
        }}
      />

      {/* Glow blobs */}
      <div
        className="absolute top-0 left-1/4 w-72 h-72 rounded-full
 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(245,158,11,0.1) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 py-14 px-8 sm:px-14 lg:px-20">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          {/* Quote icon */}
          <div
            ref={quoteRef}
            className="w-14 h-14 rounded-2xl bg-secondary-400/20
 border border-secondary-400/30 flex items-center justify-center
 mx-auto"
            style={{ opacity: 0 }}
          >
            <Quote size={24} className="text-secondary-400" />
          </div>

          {/* Divider */}
          <div
            ref={lineRef}
            className="h-px w-24 mx-auto rounded-full"
            style={{
              background:
                "linear-gradient(to right, transparent, rgba(245,158,11,0.6), transparent)",
              transform: "scaleX(0)",
            }}
          />

          {/* Quote text */}
          <blockquote
            ref={textRef}
            className="font-display font-medium text-white leading-relaxed
 italic"
            style={{
              fontSize: "clamp(1.1rem, 2.5vw, 1.5rem)",
              opacity: 0,
            }}
          >
            "{quote}"
          </blockquote>

          {/* Attribution */}
          <div className="flex items-center justify-center gap-3 pt-2">
            <div className="h-px w-8 bg-secondary-400/50" />
            <span
              className="text-secondary-400 text-sm font-semibold
 tracking-wider"
            >
              Agora Consulting
            </span>
            <div className="h-px w-8 bg-secondary-400/50" />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 STATS ROW (between cards and quote)
───────────────────────────────────────────── */
const MiniStatsRow = () => {
  const containerRef = useRef(null);
  const hasRun = useRef(false);

  const stats = [
    {
      value: 50,
      suffix: "+",
      label: "Projects",
      colorClass: "text-blue-500 border-blue-500/20 bg-blue-500/5",
    },
    {
      value: 100,
      suffix: "+",
      label: "Researchers",
      colorClass: "text-amber-500 border-amber-500/20 bg-amber-500/5",
    },
    {
      value: 15,
      suffix: "+",
      label: "Partners",
      colorClass: "text-indigo-500 border-indigo-500/20 bg-indigo-500/5",
    },
    {
      value: 2,
      suffix: "+",
      label: "Years",
      colorClass: "text-emerald-500 border-emerald-500/20 bg-emerald-500/5",
    },
  ];

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Target elements via specific class names safely
    const cards = container.querySelectorAll(".stat-card");
    const numbers = container.querySelectorAll(".stat-number");

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: container,
        start: "top 85%",
        onEnter: () => {
          if (hasRun.current) return;
          hasRun.current = true;

          /* 1. Stagger reveal cards */
          gsap.fromTo(
            cards,
            { y: 30, opacity: 0, scale: 0.9 },
            {
              y: 0,
              opacity: 1,
              scale: 1,
              stagger: 0.1,
              duration: 0.6,
              ease: "back.out(1.4)",
            },
          );

          /* 2. Count up each stat natively utilizing GSAP's text/snap engine */
          numbers.forEach((el) => {
            const targetVal = parseInt(
              el.getAttribute("data-target") || "0",
              10,
            );
            const suffix = el.getAttribute("data-suffix") || "";

            const obj = { val: 0 };
            gsap.to(obj, {
              val: targetVal,
              duration: 1.5,
              ease: "power2.out",
              onUpdate: () => {
                el.textContent = Math.round(obj.val) + suffix;
              },
            });
          });
        },
      });
    }, container);

    return () => ctx.revert();
  }, []); // Safe now because it relies on class selections within the wrapper layout

  return (
    <div ref={containerRef} className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {stats.map((stat, i) => (
        <div
          key={i}
          className={`stat-card opacity-0 relative rounded-2xl p-5 text-center overflow-hidden border ${stat.colorClass}`}
        >
          {/* Subtle Watermark BG number */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none font-mono font-black text-7xl opacity-5 select-none">
            {stat.value}
          </div>

          {/* Animated counter element */}
          <p
            data-target={stat.value}
            data-suffix={stat.suffix}
            className="stat-number font-black font-mono text-3xl mb-1"
          >
            0{stat.suffix}
          </p>

          <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
            {stat.label}
          </p>
        </div>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────
 BACKGROUND
───────────────────────────────────────────── */
const Background = () => {
  const shape1 = useRef(null);
  const shape2 = useRef(null);

  useEffect(() => {
    gsap.to(shape1.current, {
      y: -40,
      rotate: 20,
      duration: 8,
      ease: "sine.inOut",
      repeat: -1,
      yoyo: true,
    });
    gsap.to(shape2.current, {
      y: 30,
      rotate: -15,
      x: 15,
      duration: 10,
      ease: "sine.inOut",
      repeat: -1,
      yoyo: true,
      delay: 1.5,
    });
  }, []);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div
        ref={shape1}
        className="absolute top-20 right-20 w-80 h-80 rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 70%)",
          filter: "blur(50px)",
        }}
      />
      <div
        ref={shape2}
        className="absolute bottom-40 left-10 w-96 h-96 rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(245,158,11,0.04) 0%, transparent 70%)",
          filter: "blur(60px)",
        }}
      />

      {/* Grid */}
      <div className="absolute inset-0 grid-pattern opacity-[0.03]" />

      {/* Diagonal accent */}
      <div
        className="absolute top-0 right-0 w-px h-full pointer-events-none
 hidden xl:block"
        style={{
          background:
            "linear-gradient(to bottom, transparent, rgba(59,130,246,0.2), transparent)",
          marginRight: "15%",
        }}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────
 SECTION NUMBER TAG
───────────────────────────────────────────── */
const SectionTag = () => (
  <div
    className="absolute right-6 top-1/3 hidden xl:flex
 flex-col items-center gap-2 pointer-events-none z-10"
  >
    <div
      className="w-px h-16"
      style={{
        background:
          "linear-gradient(to bottom, transparent, rgba(245,158,11,0.3))",
      }}
    />
    <p
      className="font-mono text-[10px] tracking-widest text-slate-400"
      style={{ writingMode: "vertical-lr" }}
    >
      04 / WHY AGORA
    </p>
    <div
      className="w-px h-16"
      style={{
        background:
          "linear-gradient(to top, transparent, rgba(245,158,11,0.3))",
      }}
    />
  </div>
);

/* ─────────────────────────────────────────────
 MAIN WHY CHOOSE US
───────────────────────────────────────────── */
const WhyChooseUs = () => {
  const { whyChoose } = homeData;
  const [activeIndex, setActiveIndex] = useState(0);

  const sectionRef = useRef(null);
  const bgParallaxRef = useRef(null);

  /* Background parallax */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(bgParallaxRef.current, {
        yPercent: 20,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 2,
        },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  /* Auto cycle active card */
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % whyChoose.items.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [whyChoose.items.length]);

  const handleCardEnter = useCallback((index) => {
    setActiveIndex(index);
  }, []);

  return (
    <section
      ref={sectionRef}
      id="why-choose"
      className="relative overflow-hidden bg-slate-50"
    >
      {/* BG */}
      <div ref={bgParallaxRef} className="absolute inset-0">
        <Background />
      </div>

      {/* Light mode edge fades */}
      <div
        className="absolute top-0 left-0 right-0 h-32 pointer-events-none z-10 opacity-100 transition-opacity duration-500"
        style={{
          background:
            "linear-gradient(to bottom, #f8fafc 0%, transparent 100%)",
        }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none z-10 opacity-100 transition-opacity duration-500"
        style={{
          background: "linear-gradient(to top, #f8fafc 0%, transparent 100%)",
        }}
      />

      {/* Left vertical label */}
      <div
        className="absolute left-4 top-1/2 -translate-y-1/2 hidden xl:flex
 flex-col items-center gap-3 pointer-events-none z-10"
      >
        <div
          className="w-px h-16"
          style={{
            background:
              "linear-gradient(to bottom, transparent, rgba(29,78,216,0.4))",
          }}
        />
        <p
          className="text-[10px] font-semibold tracking-[0.4em] text-slate-400
 uppercase"
          style={{ writingMode: "vertical-lr" }}
        >
          Why Choose Us
        </p>
        <div
          className="w-px h-16"
          style={{
            background:
              "linear-gradient(to top, transparent, rgba(29,78,216,0.4))",
          }}
        />
      </div>

      {/* Right section tag */}
      <SectionTag />

      <div className="container-fluid relative z-20">
        {/* ── MAIN GRID ── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-2 xl:gap-16">
          {/* Left sticky column (2/5) */}
          <div className="lg:col-span-2">
            <StickyLeft data={whyChoose} activeIndex={activeIndex} />
          </div>

          {/* Right cards column (3/5) */}
          <div className="lg:col-span-3 space-y-4">
            {/* Mini stats */}
            <MiniStatsRow />

            {/* Reason cards */}
            {whyChoose.items.map((item, i) => (
              <ReasonCard
                key={item.id}
                item={item}
                index={i}
                isActive={i === activeIndex}
                onEnter={() => handleCardEnter(i)}
              />
            ))}
          </div>
        </div>

        {/* ── QUOTE BANNER ── */}
        <QuoteBanner quote={whyChoose.quote} />
      </div>
    </section>
  );
};

export default WhyChooseUs;
